import {css, html, LitElement} from 'lit';
import {customElement, property} from 'lit/decorators';
import {
  containerStyles,
  moduleSpacingStyles,
  normalizeStyles,
  typographyStyles
} from '@webs/components-library/theming';

@customElement('webs-pagination')
export class Pagination extends LitElement {

  private internalCurrentPage = 1;
  private internalTotalPages = 1;

  static styles = [
    normalizeStyles,
    containerStyles,
    moduleSpacingStyles,
    typographyStyles,
    css`
      .pagination--center ul {
        justify-content: center;
      }

      .pagination--right ul {
        justify-content: flex-end;
      }

      ul {
        display: flex;
        list-style: none;
        margin: 0;
        padding: 0;
      }

      li.page {
        border-bottom: 1px solid var(--color-grey);
      }

      a {
        color: var(--color-grey);
        display: block;
        font-family: var(--font-family-alternative);
        font-size: .7rem;
        font-weight: var(--font-weight-strong);
        line-height: 1.3;
        padding: .45rem 1rem;
        position: relative;
        text-decoration: none;
      }

      a::after {
        background: transparent;
        content: '';
        height: 2px;
        left: 0;
        position: absolute;
        top: calc(100% - 1px);
        width: 100%;
      }

      a:hover {
        color: var(--color-base);
      }

      .page a:hover::after {
        background: linear-gradient(216.4deg, var(--color-primary) 22.12%, var(--color-secondary) 110.96%);
      }

      a svg {
        transform: translateY(5px);
      }

      li.active a {
        color: var(--color-base);
      }

      li.active a:hover::after {
        background: transparent;
      }

      li.active a::after {
        background: linear-gradient(216.4deg, var(--color-primary) 22.12%, var(--color-secondary) 110.96%);
        content: '';
        height: 2px;
        left: 0;
        position: absolute;
        top: calc(100% - 1px);
        width: 100%;
      }

    `
  ];

  @property()
  position: string;

  @property({attribute: 'space-top'})
  spaceTop = 'm'

  @property({attribute: 'space-bottom'})
  spaceBottom = 'm'

  @property({attribute: 'total-pages', type: Number})
  get totalPages() : number {
    return this.internalTotalPages;
  }

  set totalPages(value: number) {
    this.internalTotalPages = value;
    this.requestUpdate();
  }

  @property({attribute: 'page', type: Number})
  set currentNumPage(value: number) {
    const oldValue = this.internalCurrentPage;
    this.internalCurrentPage = value;
    this.requestUpdate('page', oldValue);
  }

  get currentNumPage(): number {
    return this.internalCurrentPage;
  }

  @property({attribute: 'page-root-url'})
  pageRootUrl;

  @property({attribute: 'page-filter-name'})
  pageFilterName = 'list_page_num';

  @property({attribute: 'for-hubspot-blog', type: Boolean})
  isForHubspotBlog: boolean;

  get morePages(): number {
    return this.totalPages - this.currentNumPage;
  }

  protected render(): unknown {
    return html`
      <section
        class="pagination ${this.position ? 'pagination--' + this.position : ''} ${this.spaceTop ? 'module_top-padding-' + this.spaceTop : ''} ${this.spaceBottom ? 'module_bottom-padding-' + this.spaceBottom : ''}">
        <div class="container">
          <ul>

            ${this.currentNumPage > 1 ? html`
              <li>
                ${this.addPageArrow(true)}
              </li>
            ` : ''}

            ${this.morePages === 0 ? html`
              ${(this.currentNumPage - 2) === 1 ? html`
                <li class="page">
                  ${this.addPageNumber(1, this.currentNumPage - 2)}
                </li>
              ` : (this.currentNumPage - 2) > 1 ? html`
                <li class="page">
                  ${this.addPageNumber(this.currentNumPage - 2, this.currentNumPage - 2)}
                </li>
              ` : ''}
            ` : ''}


            ${(this.currentNumPage - 1) === 1 ? html`
              <li class="page">
                ${this.addPageNumber(1, this.currentNumPage - 1)}
              </li>
            ` : (this.currentNumPage - 1) > 1 ? html`
              <li class="page">
                ${this.addPageNumber(this.currentNumPage - 1, this.currentNumPage - 1)}
              </li>
            ` : ''}

            <li class="page active">
              ${this.addPageNumber(this.currentNumPage, this.currentNumPage)}
            </li>

            ${(this.currentNumPage + 1) <= this.totalPages ? html`
              <li class="page">
                ${this.addPageNumber(this.currentNumPage + 1, this.currentNumPage + 1)}
              </li>
            ` : ''
            }

            ${this.currentNumPage === 1 ? html`
              ${this.currentNumPage + 2 <= this.totalPages ? html`
                <li class="page">
                  ${this.addPageNumber(this.currentNumPage + 2, this.currentNumPage + 2)}
                </li>
              ` : ''}
            ` : ''}

            ${this.morePages > 0 ? html`
              <li>
                ${this.addPageArrow(false)}
              </li>
            ` : ''}
          </ul>
        </div>
        </div>
      </section>
    `
  }

  private addPageNumber(pageNum: number, pageDisplay: number): unknown {
    return this.isForHubspotBlog ? html`
      <a href="${this.getPageUrl(pageNum)}">${pageDisplay}</a>
    ` : html`
      <a href="javascript:;" @click="${this.onPageClick}" data-page="${pageNum}">${pageDisplay}</a>
    `;
  }

  private addPageArrow(isPrev: boolean): unknown {
    console.log(this.currentNumPage);
    const pageNum = isPrev ? (this.currentNumPage - 1) : (this.currentNumPage + 1);
    return this.isForHubspotBlog ? html`
      <a class="${isPrev ? 'prev' : 'next'}" href="${this.getPageUrl(pageNum)}">
        <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="${isPrev ? 'M7 1L1 7L7 13' : 'M1 1L7 7L1 13'}" stroke="#181A23" stroke-width="1.5" stroke-linejoin="round"/>
        </svg>
      </a>
    ` : html`
      <a href="javascript:;" class="${isPrev ? 'prev' : 'next'}" @click="${this.onPageClick}" data-page="${pageNum}">
        <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="${isPrev ? 'M7 1L1 7L7 13' : 'M1 1L7 7L1 13'}" stroke="#181A23" stroke-width="1.5" stroke-linejoin="round"/>
        </svg>
      </a>
    `
  }

  private onPageChange(page: number): void {
    this.dispatchEvent(new CustomEvent('pagechange', {
      detail: page
    }))
  }

  private onPageClick(e: Event): void {
    const target: HTMLElement = e.currentTarget as HTMLElement;
    const page = target.dataset.page;
    this.onPageChange(parseInt(page));
  }

  private getPageUrl(pageNum): string {
    return this.isForHubspotBlog ? this.createBlogPageUrl(pageNum) : this.createPageUrl(pageNum);
  }

  private createBlogPageUrl(pageNum: number): string {
    const page = window.location.href.split('/page');
    return pageNum == 1 ? page[0] : `${page[0]}/page/${pageNum}`;
  }

  private createPageUrl(pageNum: number): string {
    const parentPageArray = window.location.href.split('?');
    const currentQuery = parentPageArray[1];
    let pageUrl = parentPageArray[0];

    let operator;
    let isUsed: boolean;


    if (currentQuery) {
      const filters = currentQuery.split('&');

      for (let i = 0; i < filters.length; i++) {
        const filter: string[] = filters[i].split('=');
        const key = filter[0].replace('?', '');
        const value = filter[1];

        operator = !operator ? '?' : '&';
        pageUrl += (key === this.pageFilterName) ? `${operator}${key}=${pageNum}` : `${operator}${key}=${value}`;
        isUsed = (key === this.pageFilterName);
      }
    }
    if (!isUsed) {
      operator = !operator ? '?' : '&';
      pageUrl += `${operator}${this.pageFilterName}=${pageNum}`;
    }

    return pageUrl;
  }
}
